import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { usePlatformViewStore } from '@/store/platformViewStore';
import type { OrgRole } from '@/types';

// Role hierarchy mirrors the database's has_role() function
// (0001_initial_schema.sql §11.4) so client-side gating matches server-side
// RLS exactly — this is a UX convenience only; the real enforcement is
// always server-side (constitution Principle VI, spec FR-125).
const ROLE_RANK: Record<OrgRole, number> = {
  viewer: 0,
  user: 1,
  manager: 2,
  admin: 3,
  owner: 4,
};

function hasAtLeastRole(memberRole: OrgRole | undefined, requiredRole: OrgRole): boolean {
  if (!memberRole) return false;
  return ROLE_RANK[memberRole] >= ROLE_RANK[requiredRole];
}

interface RoleGateProps {
  /** Minimum role required (inclusive) to render the children. */
  role: OrgRole;
  children: React.ReactNode;
  /** Where to redirect when the member doesn't meet `role`. Defaults to /dashboard. */
  redirectTo?: string;
  /**
   * Render nothing instead of redirecting — for inline UI (e.g. hiding a
   * button) rather than gating a whole route.
   */
  hideInstead?: boolean;
}

export function RoleGate({ role, children, redirectTo = '/dashboard', hideInstead = false }: RoleGateProps) {
  const { member, platformAdmin } = useAuthStore();
  const { viewingOrgId } = usePlatformViewStore();
  // A platform admin visiting an org sees every admin-gated page (full
  // read-only parity, per the platform-admin plan) — they're never a real
  // member.role, so the rank check below would otherwise always fail them.
  const allowed = (platformAdmin && viewingOrgId) || hasAtLeastRole(member?.role, role);

  if (allowed) return <>{children}</>;
  if (hideInstead) return null;
  return <Navigate to={redirectTo} replace />;
}
